import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  runTransaction,
  serverTimestamp,
  writeBatch
} from "firebase/firestore";

import { db } from "../lib/firebase";

/* =====================================================
COLLECTIONS
===================================================== */

export const COLLECTIONS = {
  USERS: "users",
  PARTIES: "parties",
  ITEMS: "items",
  LEADS: "leads",
  QUOTATIONS: "quotations",
  PROFORMA_INVOICES: "proforma_invoices",
  SOA: "soa",
  COUNTERS: "counters",
  SETTINGS: "settings"
};

/* =====================================================
PREFIXES
===================================================== */

const DOC_PREFIXES = {
  lead: "LEAD",
  quotation: "QTN",
  pi: "PI",
  soa: "SOA",
  party: "PTY",
  item: "ITM"
};

/* =====================================================
DOC NUMBER GENERATOR
===================================================== */

export const generateDocNumber = async (type) => {
  const ref = doc(db, COLLECTIONS.COUNTERS, "global");

  return await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    let current = 0;

    if (snap.exists()) current = snap.data()[type] || 0;

    const next = current + 1;
    const prefix = DOC_PREFIXES[type];
    const docNo = `${prefix}${String(next).padStart(4, "0")}`;

    tx.set(ref, { [type]: next }, { merge: true });

    return docNo;
  });
};

/* =====================================================
GST
===================================================== */

export const calculateGST = (partyGST, taxable, gstPercent) => {
  const isMH = partyGST && partyGST.startsWith("27");
  const gstAmount = (taxable * gstPercent) / 100;

  if (isMH) {
    return { CGST: gstAmount / 2, SGST: gstAmount / 2, IGST: 0 };
  }

  return { CGST: 0, SGST: 0, IGST: gstAmount };
};

/* =====================================================
SNAPSHOTS
===================================================== */

export const createPartySnapshot = (party) => ({
  party_id: party.id,
  name: party.name,
  address: party.address || "",
  gst: party.gst_number || "",
  state: party.state || ""
});

export const createItemSnapshot = (item) => ({
  item_id: item.item_id || item.id || "",
  item_name: item.item_name || item.name || "",
  item_code: item.item_code || item.code || "",
  description: item.description || "",
  HSN: item.HSN || item.hsn || "",
  UOM: (item.UOM || item.uom || "").toUpperCase(),
  rate: Number(item.rate || 0),
  GST_percent: Number(item.GST_percent ?? item.gst_percent ?? 0)
});

/* =====================================================
LOCK HELPERS
===================================================== */

export const lockDocument = async (collectionName, id) => {
  const ref = doc(db, collectionName, id);
  await updateDoc(ref, { locked: true, locked_at: serverTimestamp() });
};

export const unlockDocument = async (collectionName, id) => {
  const ref = doc(db, collectionName, id);
  await updateDoc(ref, { locked: false, unlocked_at: serverTimestamp() });
};

/* =====================================================
PARTIES
===================================================== */

export const getParties = async () => {
  const q = query(collection(db, COLLECTIONS.PARTIES), orderBy("party_name", "asc"));
  const snap = await getDocs(q);

  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      ...data,
      name: data.party_name || "",
      gst_number: data.GST_number || "",
      phone: data.phone || data.mobile || "",
      email: data.email || "",
      address: data.address || "",
      state: data.state || ""
    };
  });
};

export const getParty = async (id) => {
  const snap = await getDoc(doc(db, COLLECTIONS.PARTIES, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const createParty = async (data) => {
  const partyNo = await generateDocNumber("party");

  const ref = await addDoc(collection(db, COLLECTIONS.PARTIES), {
    ...data,
    party_id: partyNo,
    party_name: data.name || data.party_name || "",
    created_at: serverTimestamp(),
    updated_at: serverTimestamp()
  });

  return ref.id;
};

export const updateParty = async (id, data) => {
  await updateDoc(doc(db, COLLECTIONS.PARTIES, id), {
    ...data,
    party_name: data.name || data.party_name || "",
    updated_at: serverTimestamp()
  });
};

export const deleteParty = async (id) => {
  await deleteDoc(doc(db, COLLECTIONS.PARTIES, id));
};

/* =====================================================
ITEMS
===================================================== */

export const getItems = async () => {
  const q = query(collection(db, COLLECTIONS.ITEMS), orderBy("item_name", "asc"));
  const snap = await getDocs(q);

  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      ...data,
      name: data.item_name || "",
      code: data.item_code || "",
      description: data.description || "",
      hsn: data.HSN || "",
      uom: data.UOM || "",
      gst_percent: Number(data.GST_percent || 0),
      rate: Number(data.rate || 0)
    };
  });
};

export const getItem = async (id) => {
  const snap = await getDoc(doc(db, COLLECTIONS.ITEMS, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const createItem = async (data) => {
  const itemNo = await generateDocNumber("item");

  const ref = await addDoc(collection(db, COLLECTIONS.ITEMS), {
    ...data,
    item_id: itemNo,
    item_name: data.name || data.item_name || "",
    created_at: serverTimestamp(),
    updated_at: serverTimestamp()
  });

  return ref.id;
};

export const updateItem = async (id, data) => {
  await updateDoc(doc(db, COLLECTIONS.ITEMS, id), {
    ...data,
    item_name: data.name || data.item_name || "",
    updated_at: serverTimestamp()
  });
};

export const deleteItem = async (id) => {
  await deleteDoc(doc(db, COLLECTIONS.ITEMS, id));
};

/* =====================================================
LEADS
===================================================== */

export const getLeads = async () => {
  const q = query(collection(db, COLLECTIONS.LEADS), orderBy("created_at", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getLead = async (id) => {
  const snap = await getDoc(doc(db, COLLECTIONS.LEADS, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const createLead = async (data) => {

  //  generate lead number using counter
  const leadNo = await generateDocNumber("lead");

  const ref = await addDoc(collection(db, COLLECTIONS.LEADS), {
    ...data,
    lead_no: leadNo,                 
    created_at: serverTimestamp(),
    updated_at: serverTimestamp()
  });

return {
  id: ref.id,
  ...data,
  lead_no: leadNo,
  created_at: new Date(),   // ✅ mimic Firestore timestamp for UI
 };
};

export const updateLead = async (id, data) => {
  await updateDoc(doc(db, COLLECTIONS.LEADS, id), {
    ...data,
    updated_at: serverTimestamp()
  });
};

export const deleteLead = async (id) => {
  await deleteDoc(doc(db, COLLECTIONS.LEADS, id));
};

/* =====================================================
QUOTATIONS
===================================================== */

export const getQuotations = async () => {
  const q = query(collection(db, COLLECTIONS.QUOTATIONS), orderBy("created_at", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getQuotation = async (id) => {
  const snap = await getDoc(doc(db, COLLECTIONS.QUOTATIONS, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const createQuotation = async (data) => {
  const quotationNo = await generateDocNumber("quotation");

  const ref = await addDoc(collection(db, COLLECTIONS.QUOTATIONS), {
    ...data,
    quotation_no: quotationNo,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp()
  });

  return { id: ref.id, quotation_no: quotationNo };
};

export const updateQuotation = async (id, data) => {
  await updateDoc(doc(db, COLLECTIONS.QUOTATIONS, id), {
    ...data,
    updated_at: serverTimestamp()
  });
};

export const deleteQuotation = async (id) => {
  await deleteDoc(doc(db, COLLECTIONS.QUOTATIONS, id));
};

/* =====================================================
PROFORMA
===================================================== */

export const getProformaInvoices = async () => {
  const q = query(collection(db, COLLECTIONS.PROFORMA_INVOICES), orderBy("created_at", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getProformaInvoice = async (id) => {
  const snap = await getDoc(doc(db, COLLECTIONS.PROFORMA_INVOICES, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const createProformaInvoice = async (data) => {
  const piNo = await generateDocNumber("pi");

  const ref = await addDoc(collection(db, COLLECTIONS.PROFORMA_INVOICES), {
    ...data,
    pi_no: piNo,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp()
  });

  return { id: ref.id, pi_no: piNo };
};

export const updateProformaInvoice = async (id, data) => {
  await updateDoc(doc(db, COLLECTIONS.PROFORMA_INVOICES, id), {
    ...data,
    updated_at: serverTimestamp()
  });
};

export const deleteProformaInvoice = async (id) => {
  await deleteDoc(doc(db, COLLECTIONS.PROFORMA_INVOICES, id));
};

/* =====================================================
SOA
===================================================== */

export const getSOAs = async () => {
  const q = query(collection(db, COLLECTIONS.SOA), orderBy("created_at", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getSOA = async (id) => {
  const snap = await getDoc(doc(db, COLLECTIONS.SOA, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const createSOA = async (data) => {
  const soaNo = await generateDocNumber("soa");

  const ref = await addDoc(collection(db, COLLECTIONS.SOA), {
    ...data,
    soa_no: soaNo,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp()
  });

  return { id: ref.id, soa_no: soaNo };
};

export const updateSOA = async (id, data) => {
  await updateDoc(doc(db, COLLECTIONS.SOA, id), {
    ...data,
    updated_at: serverTimestamp()
  });
};

export const deleteSOA = async (id) => {
  await deleteDoc(doc(db, COLLECTIONS.SOA, id));
};


/* =====================================================
SETTINGS
===================================================== */

export const getSettings = async () => {

  const ref = doc(db, COLLECTIONS.SETTINGS, "company");

  const snap = await getDoc(ref);

  if (!snap.exists()) {

    return {
      company_name: "SUNSTORE KOLHAPUR",
      logo_url: "",
      address: "",
      phone: "",
      email: "",
      gst_number: "",
      state_code: "27",
      pdf_footer:
        "Thank You for Your Opportunity!\nWe look forward to continued support in our mutual interest.\nSUNSTORE KOLHAPUR\nComputer Generated Document – No Signature Required"
    };

  }

  return snap.data();

};

export const updateSettings = async (data) => {

  const ref = doc(db, COLLECTIONS.SETTINGS, "company");

  await updateDoc(ref, {
    ...data,
    updated_at: serverTimestamp()
  });

};

export const initializeSettings = async () => {

  const ref = doc(db, COLLECTIONS.SETTINGS, "company");

  const snap = await getDoc(ref);

  if (!snap.exists()) {

    const batch = writeBatch(db);

    batch.set(ref, {
      company_name: "SUNSTORE KOLHAPUR",
      logo_url: "",
      address: "",
      phone: "",
      email: "",
      gst_number: "",
      state_code: "27",
      pdf_footer:
        "Thank You for Your Opportunity!\nWe look forward to continued support in our mutual interest.\nSUNSTORE KOLHAPUR\nComputer Generated Document – No Signature Required",
      created_at: serverTimestamp()
    });

    await batch.commit();

  }

};

/* =====================================================
CONVERSION FUNCTIONS
===================================================== */

export const convertQuotationToPI = async (quotation) => {

  const piNumber = await generateDocNumber("pi");

  const newPI = {
    pi_no: piNumber,
    party_snapshot: quotation.party_snapshot,
    items: quotation.items,
    totals: quotation.totals,
    terms: quotation.terms || {},
    status: "draft",
    source_quotation: quotation.id,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp()
  };

  const ref = await addDoc(
    collection(db, COLLECTIONS.PROFORMA_INVOICES),
    newPI
  );

  return {
    id: ref.id,
    pi_no: piNumber
  };

};

export const convertPIToSOA = async (pi) => {

  const soaNumber = await generateDocNumber("soa");

  const newSOA = {
    soa_no: soaNumber,
    party_snapshot: pi.party_snapshot,
    items: pi.items,
    totals: pi.totals,
    terms: pi.terms || {},
    status: "draft",
    source_pi: pi.id,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp()
  };

  const ref = await addDoc(
    collection(db, COLLECTIONS.SOA),
    newSOA
  );

  return {
    id: ref.id,
    soa_no: soaNumber
  };

};